// R122 — the MTR Hold Agent on the Calendar's MTR view. Registers (one import line in ../features.ts):
//   layer      mtr_holds  (load → GET /api/mtr/holds/calendar; carries `homes` for the MTR rowFilter)
//              chips: the HELD BAR per held stretch ("MTR hold · opens to STR on <date>"), the lease
//              nights ("MTR lease"), and a one-day marker on the day the next night opens
//   stat       mtr_holds  (active plans · nights held · next opening; red when a release is overdue)
//   rail       mtr_holds  (plans by next opening — the Holds list on the Calendar)
//   inspector  mtr_hold   (+ sections plan / event log, actions Place holds, Mark MTR booked, Pause / Resume,
//                          Open lease nights (admin), Cancel plan or Remove from plan, then Release now and Release
//                          a week early: the agent releases by itself, so those stay behind the ⋯)
//   section    listing.mtr_holds (this home's plans)
//   action     date.mtr_hold "Hold for MTR…" (R131: pick an open hold plan or a new one → Add homes → Preview → Confirm)
//   chip kind  mtr_hold
// Writes go through server/mtrHolds.js only; every one reloads this layer + the inspector.
import {
  registerLayer, registerStat, registerRail, registerInspector, registerSection, registerAction, registerChipKind, registerBusy, registerCss,
  type OfficeRole, type ViewId, type CalendarCtx, type ChipSpec, type RailItem, type Tone, type DateCell,
} from '../registry';
import {
  mhBusy, releaseSheet, holdFocus, api, esc, md, rangeLabel, nightsWord, toneOf, opensLine, autoLine, agentAlone, draftNoteHtml, planDetailHtml, logHtml, actionsFor, runPlanAction, openPickPlan, boxRange, MH_CSS,
  type HoldsLayer, type HoldPlan, type HoldRun, type FormHost,
} from '../../mtrHolds/shared';

export const LAYER = 'mtr_holds';
const OFFICE: OfficeRole[] = ['admin', 'director', 'manager', 'remote_ops'];
const ALL_VIEWS: ViewId[] = ['stays', 'ops', 'guests', 'mtr', 'health'];

export function holdsOf(ctx: Pick<CalendarCtx, 'layer'>): HoldsLayer | null {
  const o = ctx.layer<HoldsLayer>(LAYER);
  return o && typeof o === 'object' && Array.isArray(o.plans) && Array.isArray(o.homes) ? o : null;
}
const toneMap = (p: HoldPlan): Tone => toneOf(p);

/** The held bars (one per held stretch), the lease nights, and the next-opening marker. end is exclusive. */
export function holdChips(listingId: string, ctx: Pick<CalendarCtx, 'layer' | 'from' | 'to' | 'fmt'>): ChipSpec[] {
  const o = holdsOf(ctx);
  if (!o) return [];
  const out: ChipSpec[] = [];
  const toX = ctx.fmt.addDays(ctx.to, 1);
  for (const p of o.plans) {
    if (p.listing_id !== listingId) continue;
    const ref = { kind: 'mtr_hold', id: p.id, listing_id: p.listing_id };
    const line = opensLine(p);
    const n = p.next_open;
    const dry = p.placement === 'simulated';
    const draft = p.status === 'draft' || (p.status === 'active' && !p.placement);
    // R122 review 2: each held stretch says its OWN first opening day (opens_on from the server's schedule),
    // never the plan's next opening, which may belong to an earlier stretch. An answer without opens_on
    // (an older server) uses the next opening only on the stretch it falls in.
    const labelDay = (r: HoldRun): string | null => (r.opens_on !== undefined ? r.opens_on || null : (n && n.start >= r.start && n.start <= r.end ? n.day : null));
    // R122 review 3: the held nights a tenant record in question covers (they wait for a person, never open on their own)
    const dec = p.status === 'active' && p.needs_decision && Array.isArray(p.needs_decision.runs) ? p.needs_decision.runs : [];
    const decIn = (r: HoldRun): HoldRun[] => dec.filter(x => x.start <= r.end && x.end >= r.start);
    // R124: a minimum-stay plan's nights stay listed; its bar says "min 31" so nobody reads it as blocked
    const minTag = p.method === 'minstay' ? ` · min ${p.min_stay || 31}` : '';
    const labelFor = (r: HoldRun): string => `${labelBase(r)}${minTag}`;
    const labelBase = (r: HoldRun): string => {
      if (draft) return `MTR hold ${p.id} · draft, not placed`;
      if (p.cancel_pending) return `MTR hold${dry ? ' (dry run)' : ''} · cancelling`;
      if (decIn(r).length) return `MTR hold${dry ? ' (dry run)' : ''} · needs a decision`;
      const day = labelDay(r);
      return `MTR hold${dry ? ' (dry run)' : ''}${day ? ` · opens to STR on ${md(day)}` : ''}`;
    };
    for (const r of p.held_runs) {
      const s = r.start > ctx.from ? r.start : ctx.from;
      const e0 = ctx.fmt.addDays(r.end, 1);
      const e = e0 < toX ? e0 : toX;
      if (!(s < e)) continue;
      const day = labelDay(r);
      const opens = !draft && !p.cancel_pending && day ? (p.method === 'minstay' ? ` This stretch first comes off its PriceLabs minimum on ${md(day)}.` : ` This stretch first opens to STR on ${md(day)}.`) : '';
      const dIn = decIn(r);
      // R129: a minstay plan's nights in question keep its PriceLabs minimum (still listed), never "stay blocked"
      const kept = p.method === 'minstay' ? `keep the ${p.min_stay || 31}-night PriceLabs minimum (still listed for ${p.min_stay || 31}+ night stays)` : 'stay blocked';
      const decide = dIn.length && !p.cancel_pending ? ` Needs a decision: ${dIn.map(x => rangeLabel(x)).join(', ')} ${kept} until someone uses Mark MTR booked, Release now or ${p.group_id ? 'Remove from plan' : 'Cancel plan'}.` : '';
      out.push({
        key: `mh:${p.id}:${r.start}`, entity: { ...ref, date: s }, listing_id: listingId, start: s, end: e,
        tone: dIn.length && !p.overdue ? 'warn' : toneMap(p), glyph: '⏸', label: labelFor(r), dashed: p.placement !== 'live' || undefined, ring: !!p.overdue || undefined,
        title: `${p.id} · ${p.display_status} · held ${rangeLabel(r)} (${nightsWord(r.nights || 0)})${p.method === 'minstay' ? ` at a ${p.min_stay || 31}-night PriceLabs minimum stay (still listed on Airbnb and Vrbo)` : ''}.${opens}${decide} ${line}${p.overdue ? ` ${p.overdue.text}.` : ''} Tap for actions.`,
      });
    }
    // won_runs = nights that carry the lease tag (what was relabelled). A dry-run plan blocked nothing: say so.
    // R122 review 3: lease nights no active tenant record covers any more are named (they stay blocked; an admin can open them)
    const unb = p.lease_unbacked && Array.isArray(p.lease_unbacked.runs) ? p.lease_unbacked.runs : [];
    for (const r of p.won_runs) {
      const s = r.start > ctx.from ? r.start : ctx.from;
      const e0 = ctx.fmt.addDays(r.end, 1);
      const e = e0 < toX ? e0 : toX;
      if (!(s < e)) continue;
      const gone = !dry && unb.some(x => x.start <= r.end && x.end >= r.start);
      out.push({ key: `mh:${p.id}:won:${r.start}`, entity: { ...ref, date: s }, listing_id: listingId, start: s, end: e, tone: gone ? 'warn' : 'ok', glyph: '🔑',
        label: dry ? 'MTR lease (dry run) · not blocked' : gone ? 'MTR lease · no tenant record' : 'MTR lease · stays blocked',
        title: dry ? `${p.id} · MTR booked ${rangeLabel(r)} in a dry run: nothing is blocked in Hostaway for these nights.`
          : gone ? `${p.id} · MTR lease ${rangeLabel(r)}: these nights stay blocked ("MTR lease · ${p.id}"), but no active tenant record in MTR → Tenants covers some of them any more. Tap for details.`
            : `${p.id} · MTR booked ${rangeLabel(r)}: these nights stay blocked ("MTR lease · ${p.id}").` });
    }
    if (n && p.status === 'active' && !p.cancel_pending && n.day >= ctx.from && n.day <= ctx.to) {
      out.push({ key: `mh:${p.id}:opens`, entity: { ...ref, date: n.day }, listing_id: listingId, start: n.day, tone: 'ok', glyph: '▾', label: 'opens',
        title: p.method === 'minstay'
          ? `${p.id}: on ${md(n.day)} ${n.nights > 1 ? `${rangeLabel({ start: n.start, end: n.end })} go` : `${md(n.start)} goes`} off the PriceLabs minimum (inside the ${p.runway_weeks}-week runway).`
          : `${p.id}: on ${md(n.day)} ${n.nights > 1 ? `${rangeLabel({ start: n.start, end: n.end })} open` : `${md(n.start)} opens`} to STR (inside the ${p.runway_weeks}-week runway).` });
    }
  }
  return out;
}

export function railHolds(ctx: Pick<CalendarCtx, 'layer' | 'property'>): RailItem[] {
  const o = holdsOf(ctx);
  if (!o) return [];
  return o.plans.filter(p => p.status === 'active' || p.status === 'draft')
    .sort((a, b) => (a.overdue ? 0 : a.needs_decision ? 1 : 2) - (b.overdue ? 0 : b.needs_decision ? 1 : 2) || String((a.next_open && a.next_open.day) || '9999').localeCompare(String((b.next_open && b.next_open.day) || '9999')))
    .map(p => {
      const home = ctx.property(p.listing_id)?.name || p.listing_name || p.listing_id;
      const dec = p.status === 'active' && p.needs_decision ? p.needs_decision : null;
      return {
        key: `mh:${p.id}`, entity: { kind: 'mtr_hold', id: p.id, listing_id: p.listing_id }, listing_id: p.listing_id,
        title: `${home} · ${p.id}`,
        sub: p.overdue ? p.overdue.text : dec ? `Needs a decision: ${dec.runs.map(r => rangeLabel(r)).slice(0, 3).join(', ')} (${nightsWord(dec.nights)}) ${p.method === 'minstay' ? `keep the ${p.min_stay || 31}-night minimum` : 'stay blocked'}` : `${p.display_status} · ${nightsWord(p.held_count)} held${p.next_open ? ` · ${md(p.next_open.start)} opens to STR on ${md(p.next_open.day)}` : ''}`,
        tone: (p.overdue ? 'bad' : dec ? 'warn' : p.display_status === 'Held' ? 'info' : 'warn') as Tone, date: p.next_open ? p.next_open.day : null, search: `${home} ${p.id} mtr hold`,
      };
    });
}

/** The calendar's form host: the page's sheet; a write reloads the base calendar, this layer and the open inspector. */
function host(ctx: CalendarCtx): FormHost {
  return {
    today: ctx.asOf,
    // R122 review 2: however the page closes the sheet (its X, the dim overlay, Escape, a re-render), the
    // shared busy / dirty flags are released, or the page's auto-refresh would be vetoed for good
    open: (title, html) => ctx.openSheet(html, { title, onClose: () => { releaseSheet(); ctx.markDirty(false); } }),
    close: () => ctx.closeSheet(),
    toast: (m) => ctx.toast(m),
    setDirty: (d) => ctx.markDirty(d),
    // every hold write is mirrored into calendar_days (the base blocks + nightTaken): reload the base too
    // R131 (#18): a sheet closed mid-run hands the keyboard back before this reload redraws the inspector under it
    done: async () => { const focusAfter = holdFocus(); await ctx.reload({ base: true, layers: [LAYER], inspector: true }); focusAfter(); },
    writesLive: () => { const o = holdsOf(ctx); return o ? !!o.writes_live : true; },
    agent: () => holdsOf(ctx),
  };
}
const planOf = (d: unknown) => d as HoldPlan | null;
/** R131: the inspector's plan, with its hold plan from the layer when the answer it loaded did not carry it. */
const grouped = (p: HoldPlan, ctx: Pick<CalendarCtx, 'layer'>): HoldPlan => {
  if (p.group_id) return p;
  const o = holdsOf(ctx);
  const hit = o ? o.plans.find(x => x.id === p.id) : null;
  return hit && hit.group_id ? { ...p, group_id: hit.group_id, group_name: hit.group_name } : p;
};

// ── the layer ────────────────────────────────────────────────────────────────
registerLayer({
  id: LAYER, label: 'MTR holds', swatch: 'repeating-linear-gradient(135deg, var(--info-soft) 0 4px, var(--accent-border) 4px 7px)', roles: OFFICE, scope: 'day',
  load: async (q, signal) => {
    const r = await fetch(`/api/mtr/holds/calendar?from=${encodeURIComponent(q.from)}&to=${encodeURIComponent(q.to)}`, { signal });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j || !Array.isArray(j.plans)) throw new Error((j && j.error) || `MTR holds unavailable (${r.status})`);
    return j;
  },
  chips: (p, ctx) => holdChips(p.listing_id, ctx),
  count: (ctx) => { const o = holdsOf(ctx); return o ? o.plans.filter(p => p.status === 'active').length : null; },
});

registerStat({
  key: 'mtr_holds', label: 'MTR holds', icon: '⏸', layer: LAYER,
  compute: (ctx) => {
    const o = holdsOf(ctx);
    if (!o) return null;
    const s = o.summary;
    if (!s.active && !s.drafts) return { value: 0, sub: o.mode === 'off' ? 'agent off · + Hold for MTR on a day' : 'no hold plans' };
    const decide = o.plans.filter(p => p.status === 'active' && p.needs_decision).length;
    return {
      value: s.active, tone: s.overdue ? 'bad' : decide ? 'warn' : undefined,
      sub: s.overdue ? `${s.overdue} overdue to open` : `${decide ? `${decide} need${decide === 1 ? 's' : ''} a decision · ` : ''}${nightsWord(s.held_nights)} held${s.next_open ? ` · next opens ${md(s.next_open.day)}` : ''}${o.mode !== 'live' ? ` · ${o.mode === 'off' ? 'agent off' : 'dry run'}` : ''}`,
    };
  },
  match: {
    rows: (p, ctx) => { const o = holdsOf(ctx); return !!o && o.plans.some(x => x.listing_id === p.listing_id && (x.status === 'active' || x.status === 'draft')); },
    chips: (c) => c.entity.kind === 'mtr_hold',
  },
});

registerRail({ key: 'mtr_holds', label: 'MTR holds', layer: LAYER, items: (ctx) => railHolds(ctx) });

registerInspector({
  kind: 'mtr_hold',
  title: (ref, data) => { const p = planOf(data); return p ? `MTR hold ${p.id}` : `MTR hold ${ref.id}`; },
  sub: (_ref, data, ctx) => { const p = planOf(data); return p ? `${ctx.property(p.listing_id)?.name || p.listing_name} · ${p.display_status}${p.status === 'active' && p.needs_decision ? ' · needs a decision' : ''}${p.lease_unbacked ? ' · lease nights to check' : ''}${p.lease_check ? ' · lease nights not blocked' : ''}` : ''; },
  load: async (ref) => {
    const r = await api('GET', `/api/mtr/holds/plans/${encodeURIComponent(ref.id)}`);
    if (!r.ok || !r.j.plan) throw new Error(r.j && r.j.error ? r.j.error : 'Could not load the hold plan.');
    return r.j.plan as HoldPlan;
  },
  busy: mhBusy, reset: releaseSheet,
});
registerSection({
  inspector: 'mtr_hold', key: 'mtr_hold_plan', title: 'Hold plan', order: 10, openIn: ALL_VIEWS,
  when: (_r, d) => !!planOf(d),
  // R122 review 3: the "Needs a decision" box carries Mark MTR booked / Release now / Cancel plan; "Lease nights to check"
  // carries an admin's Open lease nights. On a phone these are the visible buttons (the action row keeps only its
  // primary). Review 4 (X3 / V8): each button prefills ONLY its own run (data-mh-decide-run), never a span across them
  // R131: said here too, so nobody reads the Release actions as a chore
  render: (_r, d, ctx) => {
    const p = grouped(planOf(d)!, ctx);
    const o = holdsOf(ctx);
    // not while a person must act (a decision, overdue nights) or nights cannot open by themselves (paused, being cancelled);
    // not on a dry-run plan while the agent writes (it opens nothing)
    const auto = o && p.status === 'active' && agentAlone(p) && (p.placement === 'live' || (p.placement === 'simulated' && !o.writes_live)) ? autoLine(o) : '';
    return `${auto ? `<p class="mh-auto" data-mh-auto>${esc(auto)}</p>` : ''}${planDetailHtml(p, ctx.asOf, { acts: true, admin: ctx.role === 'admin', sending: o ? !!o.writes_live : undefined })}${o ? draftNoteHtml(p, o) : ''}`;
  },
  bind: (root, _ref, d, ctx) => {
    const p0 = planOf(d);
    if (!p0) return;
    const p = grouped(p0, ctx);
    root.querySelectorAll<HTMLElement>('[data-mh-decide-act]').forEach(b => b.addEventListener('click', () => {
      const k = b.dataset.mhDecideAct || '';
      if (!['mark_booked', 'release_now', 'cancel', 'open_lease'].includes(k)) return;
      runPlanAction(host(ctx), p, k as Parameters<typeof runPlanAction>[2], { range: boxRange(p, k, b.dataset.mhDecideRun || null) });
    }));
  },
});
registerSection({
  inspector: 'mtr_hold', key: 'mtr_hold_log', title: 'Event log', order: 20, openIn: ALL_VIEWS,
  when: (_r, d) => !!planOf(d),
  render: (_r, d) => logHtml(planOf(d)!.log || [], 40),
});
registerSection({
  inspector: 'listing', key: 'mtr_holds', title: 'MTR holds', order: 45, openIn: ['mtr'],
  when: (ref, _d, ctx) => { const o = holdsOf(ctx); return !!o && o.plans.some(p => p.listing_id === ref.id); },
  render: (ref, _d, ctx) => {
    const o = holdsOf(ctx)!;
    return o.plans.filter(p => p.listing_id === ref.id).map(p => `<button type="button" class="mh-lrow" data-mh-open="${esc(p.id)}"><b>${esc(p.id)}</b> · ${esc(p.display_status)} · ${esc(rangeLabel({ start: p.first_night, end: p.last_night }))}<span class="mh-lrow-s">${esc(opensLine(p))}</span></button>`).join('');
  },
  bind: (root, ref, _d, ctx) => {
    root.querySelectorAll<HTMLElement>('[data-mh-open]').forEach(b => b.addEventListener('click', () => ctx.openInspector({ kind: 'mtr_hold', id: b.dataset.mhOpen || '', listing_id: ref.id })));
  },
});

// actions on a plan. R131: Place is the blue primary when it applies; otherwise the first that applies is, and that is Mark
// MTR booked, the one thing a person does. The cancel action never is (N7: a draft with the agent off has only that one, so
// it has no blue button at all). The agent releases by itself, so the Release actions come last, past the two buttons the
// row can show: they sit behind the ⋯. Exception: a Cancelling plan with nights to decide, which has fewer actions.
const ACTIONS: Array<{ key: Parameters<typeof runPlanAction>[2]; id?: string; inGroup?: boolean; label: string; order: number }> = [
  { key: 'place', label: 'Place holds…', order: 5 },
  { key: 'mark_booked', label: 'Mark MTR booked…', order: 10 },
  { key: 'pause', label: 'Pause…', order: 20 },
  { key: 'resume', label: 'Resume', order: 21 },
  { key: 'open_lease', label: 'Open lease nights…', order: 30 },
  { key: 'cancel', inGroup: false, label: 'Cancel plan…', order: 40 },
  // R131 (#37): the same action in a hold plan ends this one home's hold; its own label says so
  { key: 'cancel', id: 'remove_from_plan', inGroup: true, label: 'Remove from plan…', order: 41 },
  { key: 'release_now', label: 'Release now…', order: 50 },
  { key: 'release_next_week', label: 'Release a week early', order: 51 },
];
for (const a of ACTIONS) {
  registerAction({
    key: a.id || a.key, inspector: 'mtr_hold', label: a.label, icon: a.key === 'cancel' ? '✕' : a.key === 'pause' ? '⏸' : a.key === 'resume' ? '▶' : a.key === 'mark_booked' || a.key === 'open_lease' ? '🔑' : '↗', order: a.order,
    // Open lease nights is an admin's alone (the server answers 403 to anyone else)
    roles: a.key === 'open_lease' ? ['admin'] : OFFICE,
    primaryIn: a.key === 'place' ? ALL_VIEWS : undefined,
    danger: a.key === 'cancel' || undefined,
    when: (_r, d, ctx) => {
      const p = planOf(d);
      if (!p) return false;
      const o = holdsOf(ctx);
      if (a.inGroup !== undefined && a.inGroup !== !!grouped(p, ctx).group_id) return false;
      // no layer answer yet: the mode is not known, so Place is not hidden on a guess
      return actionsFor(p, { mode: o ? o.mode : undefined, writes_live: !!(o && o.writes_live), role: ctx.role }).some(x => x.key === a.key);
    },
    // R122 review 3 (#11): the tapped day only picks the held stretch; the form prefills that whole stretch
    run: (ref, d, ctx) => { const p = planOf(d); if (p) runPlanAction(host(ctx), grouped(p, ctx), a.key, { at: ref.date || null }); },
  });
}

// the create chooser: "Hold for MTR…" on a home + day (or the toolbar + Add, with a home picker)
registerAction({
  key: 'mtr_hold', inspector: 'date', label: 'Hold for MTR…', icon: '⏸', order: 62, roles: OFFICE,
  when: (ref, d) => !!ref.date && !((d as DateCell | null) && (d as DateCell).past),
  run: (ref, _d, ctx) => openPickPlan(host(ctx), { listing_id: ref.listing_id || null, date: ref.date || null, home: ref.listing_id ? ctx.property(ref.listing_id)?.name || null : null }),
});

// the tapped stretch's date travels with the ref, so Release now / Mark MTR booked preselect THAT stretch
registerChipKind({ kind: 'mtr_hold', legend: 'MTR hold (opens to STR as the runway reaches it)', open: (ref, _a, ctx) => ctx.openInspector({ kind: 'mtr_hold', id: ref.id, listing_id: ref.listing_id, ...(ref.date ? { date: ref.date } : {}) }) });

registerBusy('mtr_holds', mhBusy);
registerCss('mtr_holds', `${MH_CSS}
.mh-lrow { display:flex; flex-direction:column; align-items:flex-start; gap:2px; width:100%; min-height:44px; padding:8px 10px; margin-bottom:6px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); font:inherit; font-size:12.5px; color:var(--text); text-align:left; cursor:pointer; }
.mh-lrow:hover { border-color:var(--accent-border); }
.mh-lrow-s { font-size:11.5px; color:var(--text-muted); overflow-wrap:anywhere; }
`);
